import React, { useState, useEffect } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../components/common/SafeIcon';
import { getRecipients, saveRecipients } from '../utils/storage';

const { FiUserPlus, FiSearch, FiTrash2, FiEdit2, FiSave, FiX } = FiIcons;

export default function Recipients() {
  const [recipients, setRecipients] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [newRecipient, setNewRecipient] = useState({ name: '', address: '' });

  useEffect(() => {
    setRecipients(getRecipients().sort((a, b) => a.name.localeCompare(b.name)));
  }, []);

  const handleAdd = (e) => {
    e.preventDefault();
    const updated = [...recipients, { ...newRecipient, id: Date.now().toString() }];
    saveAndRefresh(updated);
    setNewRecipient({ name: '', address: '' });
  };

  const handleDelete = (id) => {
    if (window.confirm('Empfänger wirklich löschen?')) {
      const updated = recipients.filter(r => r.id !== id);
      saveAndRefresh(updated);
    }
  };

  const handleUpdate = (id, data) => {
    const updated = recipients.map(r => r.id === id ? { ...r, ...data } : r);
    saveAndRefresh(updated);
    setEditingId(null);
  };

  const saveAndRefresh = (data) => {
    saveRecipients(data);
    setRecipients(data.sort((a, b) => a.name.localeCompare(b.name)));
  };

  const filtered = recipients.filter(r => 
    r.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold text-slate-900">Empfängerverwaltung</h2>
          <p className="text-slate-500">Kundenstammdaten verwalten</p>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        <div className="bg-white p-6 rounded-2xl ring-1 ring-slate-200 shadow-sm h-fit">
          <h3 className="font-bold mb-4 flex items-center gap-2">
            <SafeIcon icon={FiUserPlus} className="text-blue-600" /> Neuer Empfänger
          </h3>
          <form onSubmit={handleAdd} className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">Name / Firma</label>
              <input 
                required
                className="w-full px-4 py-2 rounded-lg ring-1 ring-slate-200 focus:ring-2 focus:ring-blue-500 outline-none"
                value={newRecipient.name}
                onChange={e => setNewRecipient({...newRecipient, name: e.target.value})}
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">Anschrift</label>
              <textarea 
                required
                rows="3"
                className="w-full px-4 py-2 rounded-lg ring-1 ring-slate-200 focus:ring-2 focus:ring-blue-500 outline-none resize-none"
                value={newRecipient.address}
                onChange={e => setNewRecipient({...newRecipient, address: e.target.value})}
              />
            </div>
            <button type="submit" className="w-full bg-blue-600 text-white py-2 rounded-lg font-bold hover:bg-blue-700 transition-colors">
              Hinzufügen
            </button>
          </form>
        </div>

        <div className="md:col-span-2 space-y-4">
          <div className="relative">
            <SafeIcon icon={FiSearch} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
            <input 
              type="text" 
              placeholder="Kunden suchen..." 
              className="w-full pl-12 pr-4 py-3 rounded-xl border-none ring-1 ring-slate-200 focus:ring-2 focus:ring-blue-500 outline-none bg-white"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="bg-white rounded-2xl ring-1 ring-slate-200 overflow-hidden">
            <div className="divide-y divide-slate-100">
              {filtered.map(r => (
                <div key={r.id} className="p-4 hover:bg-slate-50 transition-colors">
                  {editingId === r.id ? (
                    <div className="space-y-3">
                      <input 
                        className="w-full px-3 py-1.5 rounded border"
                        value={r.name}
                        onChange={e => setRecipients(recipients.map(x => x.id === r.id ? {...x, name: e.target.value} : x))}
                      />
                      <textarea 
                        className="w-full px-3 py-1.5 rounded border"
                        value={r.address}
                        onChange={e => setRecipients(recipients.map(x => x.id === r.id ? {...x, address: e.target.value} : x))}
                      />
                      <div className="flex gap-2">
                        <button onClick={() => handleUpdate(r.id, r)} className="text-green-600 flex items-center gap-1 text-sm font-bold">
                          <SafeIcon icon={FiSave} /> Speichern
                        </button>
                        <button onClick={() => setEditingId(null)} className="text-slate-400 flex items-center gap-1 text-sm font-bold">
                          <SafeIcon icon={FiX} /> Abbruch
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex justify-between items-start">
                      <div>
                        <h4 className="font-bold text-slate-900">{r.name}</h4>
                        <p className="text-sm text-slate-500 whitespace-pre-line">{r.address}</p>
                      </div>
                      <div className="flex gap-2">
                        <button onClick={() => setEditingId(r.id)} className="p-2 text-slate-400 hover:text-blue-600">
                          <SafeIcon icon={FiEdit2} />
                        </button>
                        <button onClick={() => handleDelete(r.id)} className="p-2 text-slate-400 hover:text-red-600">
                          <SafeIcon icon={FiTrash2} />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
              {filtered.length === 0 && (
                <div className="p-8 text-center text-slate-400">Keine Empfänger gefunden</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}